import { useState } from "react"
import { IconCapture, IconPlus } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from "@/components/ui/responsive-modal"
import type { CalibrationPoint, SensorReading } from "@/types/device"

interface CalibrationCaptureSheetProps {
  reading: SensorReading
  points: CalibrationPoint[]
  onAddPoint: (rawSg: number, referenceSg: number, temperature: number) => void
}

export function CalibrationCaptureSheet({
  reading,
  points,
  onAddPoint,
}: CalibrationCaptureSheetProps) {
  const [open, setOpen] = useState(false)
  const [referenceSg, setReferenceSg] = useState("")

  const hasReading =
    reading.specificGravity !== null && reading.temperature !== null
  const canCapture = hasReading && referenceSg.trim() !== ""

  const handleCapture = () => {
    if (!canCapture) return
    const refSg = parseFloat(referenceSg)
    if (isNaN(refSg) || refSg <= 0) return
    onAddPoint(reading.specificGravity!, refSg, reading.temperature!)
    setReferenceSg("")
  }

  return (
    <>
      <Button
        size="lg"
        className="w-full"
        onClick={() => setOpen(true)}
      >
        <IconPlus className="size-4" />
        Capture point
      </Button>

      <ResponsiveModal open={open} onOpenChange={setOpen}>
        <ResponsiveModalContent>
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Capture Calibration Point</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Enter the known reference SG and capture the current device reading
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <div className="space-y-4 px-4 pb-2 sm:px-0">
            <div className="grid gap-2">
              <Label htmlFor="reference-sg">Reference SG (known value)</Label>
              <Input
                id="reference-sg"
                type="number"
                step="0.001"
                min="0.990"
                max="1.200"
                placeholder="1.050"
                value={referenceSg}
                onChange={(e) => setReferenceSg(e.target.value)}
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label className="text-xs text-muted-foreground">Current Raw SG</Label>
                <div className="flex h-9 items-center rounded-md border bg-muted/50 px-3 tabular-nums">
                  {reading.specificGravity?.toFixed(4) ?? "--"}
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs text-muted-foreground">Current Temp</Label>
                <div className="flex h-9 items-center rounded-md border bg-muted/50 px-3 tabular-nums">
                  {reading.temperature?.toFixed(1) ?? "--"}°
                </div>
              </div>
            </div>

            {!hasReading && (
              <p className="text-sm text-muted-foreground">
                Waiting for a reading from your device. Once one arrives you can
                capture against it.
              </p>
            )}

            <Button onClick={handleCapture} disabled={!canCapture} className="w-full">
              <IconCapture className="size-4" />
              Capture
            </Button>

            {points.length > 0 && (
              <div className="space-y-2 pt-2">
                <Label className="text-xs text-muted-foreground">
                  Captured this session ({points.length} point{points.length !== 1 ? "s" : ""})
                </Label>
                <ul className="space-y-1.5">
                  {points.map((point, index) => {
                    const offset = point.reference_sg - point.raw_sg
                    return (
                      <li
                        key={`${point.captured_at}-${index}`}
                        data-slot="captured-point"
                        className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2 text-sm"
                      >
                        <span className="tabular-nums">
                          ref {point.reference_sg.toFixed(4)} · raw {point.raw_sg.toFixed(4)}
                        </span>
                        <span
                          className={`tabular-nums ${offset >= 0 ? "text-green-600" : "text-red-600"}`}
                        >
                          {offset >= 0 ? "+" : ""}
                          {offset.toFixed(4)}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </div>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Done
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </>
  )
}
